"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createResourceLoader = exports.getScriptTag = void 0;
const utils_1 = require("../utils");
const evaluateBundle_1 = require("./evaluateBundle");
const loadAssets_1 = require("./loadAssets");
const RETRY_TIMES = 3;
/**
 * we need to pass hot bundle id for AMD cases
 * otherwise hot bundle will never be executed
 */
const HOT_BUNDLE_ID = 'hot';
function getScriptTag(url) {
    if (!utils_1.isBrowserMainThread()) {
        return null;
    }
    const element = document.querySelector(`script[src="${url}"]`);
    return element;
}
exports.getScriptTag = getScriptTag;
function createPromiseGuard() {
    const requests = new Map();
    return (id, { factory, persistent = true, }) => __awaiter(this, void 0, void 0, function* () {
        if (requests.has(id)) {
            return requests.get(id);
        }
        const pending = factory();
        requests.set(id, pending);
        try {
            const model = yield pending;
            if (!persistent) {
                requests.delete(id);
            }
            return model;
        }
        catch (e) {
            if (requests.has(id)) {
                requests.delete(id);
            }
            throw e;
        }
    });
}
function withRetry({ times, task, }) {
    let counter = 0;
    return new Promise((resolve, reject) => {
        const run = () => __awaiter(this, void 0, void 0, function* () {
            try {
                const response = yield task();
                resolve(response);
            }
            catch (e) {
                counter++;
                if (counter >= times) {
                    reject(e);
                }
                else {
                    run();
                }
            }
        });
        run();
    });
}
const _fetcher = (url) => __awaiter(void 0, void 0, void 0, function* () {
    const response = yield fetch(url);
    return response.text();
});
function createResourceLoader({ options, globals, fetcher = _fetcher, }) {
    const contexts = {};
    const loadResource = ({ id, url }) => withRetry({
        times: RETRY_TIMES,
        task: () => __awaiter(this, void 0, void 0, function* () {
            if (options && options.useScriptsInsteadOfEval && utils_1.isBrowser()) {
                const script = document.createElement('script');
                script.src = url;
                try {
                    const pending = new Promise((resolve, reject) => {
                        script.addEventListener('load', () => resolve());
                        script.addEventListener('error', () => reject());
                    });
                    document.head.appendChild(script);
                    yield pending;
                }
                catch (e) {
                    script.remove();
                    throw e;
                }
            }
            else {
                const bundle = yield fetcher(url);
                evaluateBundle_1.evaluateBundle(bundle, {
                    id,
                    globals,
                    contexts,
                    useExperimentalEval: options && options.useExperimentalEval,
                });
            }
        }),
    });
    const getModelByBundleID = (id) => {
        if (utils_1.isServer() && evaluateBundle_1.isOriginalRequireFunctionAvailable()) {
            return evaluateBundle_1.getModelFromContext(id, contexts[id]);
        }
        else {
            return evaluateBundle_1.getModel(id);
        }
    };
    return {
        fetch({ url }) {
            return __awaiter(this, void 0, void 0, function* () {
                return fetcher(url);
            });
        },
        fetchLibraryManifest({ url }) {
            return __awaiter(this, void 0, void 0, function* () {
                const response = yield fetcher(url);
                return JSON.parse(response);
            });
        },
        createBundleLoader({ environment }) {
            const guard = createPromiseGuard();
            let pendingEnvironmentPromise = null;
            const fetchEnvironment = () => __awaiter(this, void 0, void 0, function* () {
                if (pendingEnvironmentPromise) {
                    return pendingEnvironmentPromise;
                }
                const load = ({ id, url }) => {
                    if (!url) {
                        return;
                    }
                    const element = getScriptTag(url);
                    if (element) {
                        return Promise.resolve();
                    }
                    return loadResource({
                        id,
                        url,
                    });
                };
                pendingEnvironmentPromise = Promise.all([
                    load({ url: environment.runtime }),
                    utils_1.isBrowser()
                        ? load({ id: HOT_BUNDLE_ID, url: environment.hot })
                        : null,
                ]).then(() => 
                /**
                 * Trigger hot bundle for requirejs env.
                 * otherwise – hot bundle will be loaded but never initialized
                 **/ getModelByBundleID(HOT_BUNDLE_ID));
                return pendingEnvironmentPromise;
            });
            return ({ url, id, assets = [] }) => __awaiter(this, void 0, void 0, function* () {
                /**
                 * `loadingAssetsPromise` – not sure we should wait until CSS is loaded,
                 * since it may cause waterfall
                 */
                if (assets) {
                    loadAssets_1.loadAssets(assets.filter(a => !!a));
                }
                if (id && (utils_1.isBrowser() || utils_1.isWorker())) {
                    const model = yield evaluateBundle_1.getModel(id);
                    if (model) {
                        return model;
                    }
                }
                yield (url
                    ? guard(url, {
                        persistent: !utils_1.LOCAL_DEV_HOSTS.some(host => url.startsWith(host)),
                        factory: () => __awaiter(this, void 0, void 0, function* () {
                            if (environment) {
                                yield fetchEnvironment();
                            }
                            return loadResource({
                                id,
                                url,
                            });
                        }),
                    })
                    : null);
                if (!id) {
                    return null;
                }
                return getModelByBundleID(id);
            });
        },
    };
}
exports.createResourceLoader = createResourceLoader;
//# sourceMappingURL=createResourceLoader.js.map